import { inject, Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { finalize, map, Observable, shareReplay, tap } from 'rxjs'
import { DroitApplicatifCode, User } from '../models/user'
import { UserHttp } from '../data-access/http/user.http'
import { UserState } from '../data-access/state/user.state'
import { UserDto } from '../data-access/dto/user.dto'
import { MissingAccessError } from '../models/error/missing-access.error'
import { UserMapper } from './mapper/user.mapper'
import { DroitApplicatifMapper } from './mapper/droit-applicatif.mapper'
import { Chantier } from '../models/chantier'
import { ChantierHttp } from '../data-access/http/chantier.http'
import { ChantierMapper } from './mapper/chantier.mapper'


@Injectable({ providedIn: 'root' })
export class UserDomain {
  private readonly userHttp = inject(UserHttp)
  private readonly chantierHttp = inject(ChantierHttp)
  private readonly userState = inject(UserState)
  private readonly router = inject(Router)
  private pendingLoad$?: Observable<UserDto>

  // Selectors
  connectedUser$: Observable<User> = this.userState.store
    .select(UserState.getConnectedUser)
    .pipe(map(UserMapper.dtoToModel))

  userChantiers$: Observable<Chantier[]> = this.userState.store
    .select(UserState.getUserChantiers)
    .pipe(map(ChantierMapper.dtosToModels))
  userDroitsApplicatifsCodes$: Observable<DroitApplicatifCode[]> = this.userState.store
    .select(UserState.getDroitsApplicatifs)
    .pipe(
      map(DroitApplicatifMapper.dtosToModel),
      map((droitApplicatifs) => droitApplicatifs.map((droit) => droit.code)),
    )

  userHasOneOfTheseDroitsApplicatifs$ = (...droitsApplicatifsCodes: DroitApplicatifCode[]): Observable<boolean> =>
    this.userState.store.select(UserState.userHasOneOfTheseDroitsApplicatifs(droitsApplicatifsCodes))

  connectedUser(): User {
    return UserMapper.dtoToModel(this.userState.store.selectSnapshot(UserState.getConnectedUser))
  }

  userHasOneOfTheseDroitsApplicatifs(...droitsApplicatifs: DroitApplicatifCode[]): boolean {
    return this.userState.store.selectSnapshot(UserState.userHasOneOfTheseDroitsApplicatifs(droitsApplicatifs))
  }

  // Actions

  loadConnectedUser(codeChantier?: string): Observable<UserDto> {
    if (this.pendingLoad$) {
      return this.pendingLoad$
    }

    this.pendingLoad$ = this.userHttp.connectUser(codeChantier).pipe(
      tap({
        next: (connectedUser) =>
          this.userState.store.update((state) => ({
            ...state,
            connectedUser,
            userChantiers: connectedUser.chantiers,
          })),
        error: () => this.router.navigate(['/user-not-authenticated']),
      }),
      shareReplay(1),
      finalize(() => {
        this.pendingLoad$ = undefined
      }),
    )

    return this.pendingLoad$
  }

  checkUserHasOneOfTheseRoles(...roles: DroitApplicatifCode[]): void {
    if (!this.userState.store.selectSnapshot(UserState.userHasOneOfTheseDroitsApplicatifs(roles))) {
      throw new MissingAccessError(roles)
    }
  }
}
